class ProductGalleryZoom extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.zoomWrappers = [];
    this.dragImg = new Image();
    this.startX = null;
    this.startY = null;
    this.positionX = 0;
    this.positionY = 0;
    
    // Bind methods
    this.initSection = this.initSection.bind(this);
    this.sliderCloser = this.sliderCloser.bind(this);
    this.scrollToSlide = this.scrollToSlide.bind(this);
    this.setZoomState = this.setZoomState.bind(this);
    this.draggableZoomStart = this.draggableZoomStart.bind(this);
    this.draggableZoomMove = this.draggableZoomMove.bind(this);
    this.draggableZoomEnd = this.draggableZoomEnd.bind(this);
    this.addImageClickListeners = this.addImageClickListeners.bind(this);
    this.handleImageClick = this.handleImageClick.bind(this);
  }

  connectedCallback() {
    this.zoomWrappers = Array.from(this.querySelectorAll('.js-product-gallery-zoom__container'));
    this.buttonCloser = this.querySelector('.js-product-gallery-zoom__closer');
    
    // Defer initialization until user interaction
    ['scroll', 'touchstart', 'mousemove', 'click'].forEach(event => {
      document.addEventListener(event, this.initSection, { once: true });
    });
    
    this.zoomWrappers.forEach(wrapper => {
      wrapper.addEventListener('click', this.setZoomState);
    });
    
    this.buttonCloser.addEventListener('click', this.sliderCloser);
    
    // Make product images clickable
    this.addImageClickListeners();
    
    // NUCLEAR OPTION: Move to body for proper z-index
    setTimeout(() => {
      if (this.parentNode !== document.body) {
        this.originalParent = this.parentNode;
        document.body.appendChild(this);
        
        // Force highest z-index and positioning
        this.style.zIndex = '999999';
        this.style.position = 'fixed';
        this.style.top = '0';
        this.style.left = '0';
        this.style.width = '100vw';
        this.style.height = '100vh';
      }
    }, 100);
  }

  addImageClickListeners() {
    // Find all product gallery images and make them clickable
    const productImages = document.querySelectorAll('[data-slide]');
    productImages.forEach(image => {
      image.addEventListener('click', this.handleImageClick);
    });
  }

  handleImageClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.scrollToSlide(event.currentTarget);
  }

  initSection() {
    if (typeof Flickity !== 'undefined') {
      this.slider = new Flickity(this.querySelector('.js-product-gallery-zoom__wrapper'), {
        cellAlign: 'center',
        cellSelector: '.js-product-gallery-zoom__cell',
        contain: true,
        prevNextButtons: true,
        pageDots: false,
        draggable: false
      });
      
      this.slider.on('change', () => {
        this.zoomWrappers.forEach(wrapper => {
          wrapper.setAttribute('data-zoom', '0');
          wrapper.querySelector('img').style.transform = '';
        });
      });
    }
  }

  sliderCloser() {
    document.body.style.overflowY = 'auto';
    this.classList.remove('js-active');
  }

  scrollToSlide(element) {
    this.classList.add('js-active');
    document.body.style.overflowY = 'hidden';
    
    if (this.slider) {
      const slideIndex = parseInt(element.getAttribute('data-slide'));
      this.slider.resize();
      this.slider.select(slideIndex, false, true);
      this.slider.focus();
    }
  }

  setZoomState(event) {
    const container = event.target.closest('.js-product-gallery-zoom__container');
    if (!container) return;
    
    const currentZoom = container.getAttribute('data-zoom');
    const image = container.querySelector('img');
    
    // Reset drag position
    this.startX = null;
    this.startY = null;
    this.positionX = 0;
    this.positionY = 0;
    image.style.transform = '';
    
    switch(currentZoom) {
      case '0':
        container.setAttribute('data-zoom', '1');
        container.addEventListener('dragstart', this.draggableZoomStart);
        container.addEventListener('drag', this.draggableZoomMove);
        container.addEventListener('dragend', this.draggableZoomEnd);
        break;
      case '1':
        container.setAttribute('data-zoom', '2');
        break;
      case '2':
        container.setAttribute('data-zoom', '0');
        container.removeEventListener('dragstart', this.draggableZoomStart);
        container.removeEventListener('drag', this.draggableZoomMove);
        container.removeEventListener('dragend', this.draggableZoomEnd);
        break;
    }
  }

  draggableZoomStart(event) {
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dragImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    event.dataTransfer.setDragImage(this.dragImg, this.startX, this.startY);
  }

  draggableZoomMove(event) {
    const container = event.target.closest('.js-product-gallery-zoom__container');
    if (!container) return;
    
    const currentZoom = container.getAttribute('data-zoom');
    const image = container.querySelector('img');
    const deltaX = this.positionX + (this.startX - event.clientX);
    const deltaY = this.positionY + (this.startY - event.clientY);
    
    if (deltaX - this.positionX !== this.startX) {
      const scale = currentZoom === '1' ? 2 : 2.6;
      image.style.transform = `translate(${-deltaX}px, ${-deltaY}px) scale(${scale})`;
    }
  }

  draggableZoomEnd(event) {
    const container = event.target.closest('.js-product-gallery-zoom__container');
    if (!container) return;
    
    const currentZoom = container.getAttribute('data-zoom');
    const image = container.querySelector('img');
    const deltaX = this.positionX + (this.startX - event.clientX);
    const deltaY = this.positionY + (this.startY - event.clientY);
    const scale = currentZoom === '1' ? 2 : 2.6;
    
    image.style.transform = `translate(${-deltaX}px, ${-deltaY}px) scale(${scale})`;
    this.positionX = deltaX;
    this.positionY = deltaY;
  }

  disconnectedCallback() {
    // Clean up event listeners
    ['scroll', 'touchstart', 'mousemove', 'click'].forEach(event => {
      document.removeEventListener(event, this.initSection, { once: true });
    });
    
    this.zoomWrappers.forEach(wrapper => {
      wrapper.removeEventListener('click', this.setZoomState);
    });
    
    this.buttonCloser.removeEventListener('click', this.sliderCloser);
    
    if (this.slider) {
      this.slider.destroy();
    }
  }
}

// Register the custom element with the CORRECT name
if (!customElements.get('product-gallery-zoom')) {
  customElements.define('product-gallery-zoom', ProductGalleryZoom);
}